// auth-store.js — session store (Brief 188 upgrade from the token-only stub).
//
// Brief 188 landed the real login round-trip (crm-client.js + kfe-login.js)
// against crm-staging, so this module now holds the full normalised session
// crmWebLogin() returns, not just a bare token. Sync-engine wiring (Brief
// 025's successor) and the device-switch UI (Brief 029) are separate briefs
// that will read/extend this store further.
//
// Storage stays in localStorage — no httpOnly cookie / native keychain
// equivalent exists on the web platform. Callers should depend only on the
// API below, not the storage detail.

// Imports crm-client for signOutFull()'s server-side invalidation. That makes
// auth-store <-> crm-client a module cycle (crm-client imports getSession here
// for the Bearer header). It is safe: neither module reads the other's
// bindings at evaluation time, and every cross-module binding is a hoisted
// `export function`, so whichever side evaluates first sees the other's
// functions already initialised.
import { crmLogout } from "./crm-client.js";

const SESSION_KEY = "kfe_auth_session";
// Pre-Brief-188 storage key. Read as a fallback so a caller that only ever
// used setToken() (never setSession()) still works; cleared the first time
// setSession() runs so the two representations can't drift.
const LEGACY_TOKEN_KEY = "kfe_auth_token";

/**
 * @typedef {object} AuthSessionUser
 * @property {string|number} [id]
 * @property {string} [username]
 * @property {string} [name]
 * @property {string} [licenseNum]
 * @property {string} [licenseEndDate]
 */
/**
 * @typedef {object} AuthSessionInfo
 * @property {string|number} [id]
 * @property {string} [clientType]
 */
/**
 * @typedef {object} AuthSessionDevice
 * @property {string|number} [customerDeviceId]
 * @property {string} [uaid]
 * @property {string} [deviceModel]
 */
/**
 * @typedef {object} AuthSession
 * @property {string} token
 * @property {string} [tokenType]
 * @property {number} [expiresIn] seconds (e.g. 7200)
 * @property {number} issuedAt client-clock ms, stamped by setSession()
 * @property {AuthSessionUser} [user]
 * @property {AuthSessionInfo} [session]
 * @property {AuthSessionDevice} [device]
 */

/** @returns {AuthSession | null} */
export function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Persist the normalised crmWebLogin() payload as the active session. Stamps
 * `issuedAt` with the client clock so the refresh scheduler can compare
 * against `expiresIn` (7200s) — see isRefreshDue() below, which is the one
 * reader of that pair (Brief 194).
 * @param {Omit<AuthSession, "issuedAt"> & { issuedAt?: number }} session
 */
export function setSession(session) {
  const withIssuedAt = { ...session, issuedAt: session.issuedAt ?? Date.now() };
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(withIssuedAt));
    // Drop the legacy key so getToken() has exactly one source of truth going
    // forward — see LEGACY_TOKEN_KEY docblock above.
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch { /* private mode etc. — no-op */ }
}

/** @returns {string | null} */
export function getToken() {
  const session = getSession();
  if (session?.token) return session.token;
  try { return localStorage.getItem(LEGACY_TOKEN_KEY); }
  catch { return null; }
}

/** @returns {boolean} */
export function isSignedIn() {
  return getToken() !== null;
}

/**
 * Legacy setter, preserved for API compatibility. Prefer setSession() for
 * any new caller — it carries the full user/session/device payload.
 * @param {string} token
 */
export function setToken(token) {
  try { localStorage.setItem(LEGACY_TOKEN_KEY, token); }
  catch { /* private mode etc. — no-op */ }
}

// ---------------------------------------------------------------------------
// Last username (Brief 200) — iOS's getLastUserName, ported
// ---------------------------------------------------------------------------

/**
 * localStorage key holding the last username that signed in SUCCESSFULLY on
 * this device, so the login screen can pre-fill its username box.
 *
 * iOS does exactly this: `KFELoginViewController.viewDidLoad` (line 72) sets
 * `userNameTF.text = [KFELoginManager getLastUserName]`, which reads the
 * `kUsername` row of the persisted progress settings
 * (`KFELoginManager.m:70-74`) — written at login success, never cleared on
 * logout. The password box is left empty there and is left empty here: the
 * password is never persisted in any form (a test asserts it), so there is
 * nothing to pre-fill it from even by accident.
 *
 * localStorage, not sessionStorage — unlike the expiry notice above, the point
 * of this one is that it survives the tab being closed. It is deliberately NOT
 * cleared by clearToken()/signOut(): a learner who signs out and back in is
 * exactly who the pre-fill is for.
 *
 * Its own key rather than a field on the session, because the session is
 * deleted on sign-out and this must outlive it.
 */
export const LAST_USERNAME_KEY = "kfe_last_username";

/**
 * The last username that signed in successfully on this device, or "" when
 * nobody has yet (so a caller can assign it straight into an input's value).
 * @returns {string}
 */
export function getLastUsername() {
  try { return localStorage.getItem(LAST_USERNAME_KEY) ?? ""; }
  catch { return ""; }
}

/**
 * Remember a username for the next visit to the login screen. Called only on
 * a SUCCESSFUL login — remembering a username that failed to authenticate
 * would pre-fill a typo, which iOS does not do either (it writes at login
 * success, through the progress-settings row).
 * @param {string} username
 */
export function setLastUsername(username) {
  const trimmed = (username ?? "").trim();
  if (!trimmed) return;
  try { localStorage.setItem(LAST_USERNAME_KEY, trimmed); }
  catch { /* private mode etc. — the learner just types it again */ }
}

// ---------------------------------------------------------------------------
// Expiry notice (Brief 194)
// ---------------------------------------------------------------------------

/**
 * sessionStorage key carrying "the last sign-out was an expiry, not something
 * the learner did". The app shell sets it immediately before signing out for
 * `reason === "expired"`; the login screen takes it (read-and-clear) on mount
 * and shows the expiry notice instead of a bare login form.
 *
 * sessionStorage, not localStorage: the notice belongs to THIS tab's journey
 * to the login screen and must not resurface in a tab opened tomorrow.
 */
export const SESSION_EXPIRED_NOTICE_KEY = "kfe_session_expired_notice";

/** Arm the "your session has expired" notice for the next login-screen mount. */
export function markSessionExpiredNotice() {
  try { sessionStorage.setItem(SESSION_EXPIRED_NOTICE_KEY, "1"); }
  catch { /* private mode etc. — the learner just sees a plain login form */ }
}

/**
 * Read-and-clear the expiry notice. One-shot: a reload of /login after the
 * notice has been shown must not show it again, since by then the learner has
 * seen it and may simply be typing.
 * @returns {boolean}
 */
export function takeSessionExpiredNotice() {
  try {
    const armed = sessionStorage.getItem(SESSION_EXPIRED_NOTICE_KEY) === "1";
    if (armed) sessionStorage.removeItem(SESSION_EXPIRED_NOTICE_KEY);
    return armed;
  } catch { return false; }
}

// ---------------------------------------------------------------------------
// Token refresh (Brief 194)
// ---------------------------------------------------------------------------

/**
 * The `?dev=1` mock session's token (written by src/app.js). It is NOT a real
 * CRM credential: the server rejects it with 401, and there is nothing to
 * refresh. Every refresh path below short-circuits on it so local dev and the
 * synthetic-learner harness behave exactly as they did before Brief 194.
 *
 * MUST stay in step with the literal in `src/app.js`'s `?dev=1` handler —
 * `tests/data/auth-store.test.mjs` asserts the two agree.
 */
export const DEV_BYPASS_TOKEN = "dev-bypass";

/**
 * Refresh this many ms BEFORE the token's nominal expiry. Five minutes against
 * a 7200s token: comfortably more than one heartbeat interval (60s), so the
 * proactive refresh gets ~5 chances to land before the learner could ever see
 * a 401. Override per-call (sync-engine threads it through its `deps` seam).
 */
export const REFRESH_MARGIN_MS = 5 * 60_000;

/** @returns {boolean} whether the live session is the `?dev=1` mock, not a real CRM session */
export function isDevSession() {
  return getSession()?.token === DEV_BYPASS_TOKEN;
}

/**
 * Is the live token close enough to expiry that we should renew it now?
 *
 * CLOCK SKEW (Brief 194 gotcha): `issuedAt` is the CLIENT clock, so it is only
 * ever compared against the client clock — elapsed time relative to itself.
 * Nothing here reads a server timestamp, so a learner whose laptop is twenty
 * minutes off still refreshes on schedule.
 *
 * False when there is no session, when `expiresIn` is missing (nothing to
 * schedule against), or for the dev-bypass mock.
 *
 * @param {{ now?: number, marginMs?: number }} [opts]
 * @returns {boolean}
 */
export function isRefreshDue({ now = Date.now(), marginMs = REFRESH_MARGIN_MS } = {}) {
  const session = getSession();
  if (!session?.token) return false;
  if (session.token === DEV_BYPASS_TOKEN) return false;
  if (typeof session.expiresIn !== "number" || !(session.expiresIn > 0)) return false;
  const issuedAt = typeof session.issuedAt === "number" ? session.issuedAt : 0;
  const elapsed = now - issuedAt;
  return elapsed >= session.expiresIn * 1000 - marginMs;
}

/**
 * Write a refreshed credential over the live session, keeping everything the
 * refresh response does NOT carry (user, session, device). `issuedAt` is
 * re-stamped so the expiry schedule restarts from now.
 *
 * There is NO refresh token on the client to rotate (Allen, 2026-11-01): the
 * server holds the refresh token against the session and the client presents
 * only the Bearer, so a refresh response carries a credential block and
 * nothing to store beside it.
 *
 * No-ops and returns false when there is no live session to refresh, or when
 * the live session is the dev-bypass mock.
 *
 * @param {{ token?: string, tokenType?: string, expiresIn?: number }} refreshed
 * @param {{ now?: number }} [opts]
 * @returns {boolean} whether the session was written
 */
export function applyRefreshedSession(refreshed, { now = Date.now() } = {}) {
  const session = getSession();
  if (!session?.token) return false;
  if (session.token === DEV_BYPASS_TOKEN) return false;
  if (!refreshed?.token) return false;
  setSession({
    ...session,
    token: refreshed.token,
    tokenType: refreshed.tokenType ?? session.tokenType,
    expiresIn: typeof refreshed.expiresIn === "number" ? refreshed.expiresIn : session.expiresIn,
    issuedAt: now,
  });
  return true;
}

/**
 * Clear only the session. Per Brief 014 (still the contract): signing out
 * must NOT wipe learner data (mmuStreaks, itemProgress, settings) — that's
 * tied to learnerId and gets re-fetched on next sign-in. Just drop the
 * credential (both the current session key and the legacy token key).
 */
export function clearToken() {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch { /* no-op */ }
}

/**
 * Sign out LOCALLY ONLY: clear session, announce it, and let the caller route
 * to /login. No server call.
 *
 * This stays the right entry point for a session that the server has ALREADY
 * invalidated — crm-client's onSessionInvalid listeners fire on exactly that
 * case (a 401/20000 whose refresh failed, or a 409/91049), and calling
 * crmLogout() there would only earn a second 401. Learner-initiated sign-out
 * should call signOutFull() instead.
 */
export function signOut() {
  clearToken();
  document.dispatchEvent(new Event("kfe:auth-changed"));
}

/**
 * Sign out of BOTH the server (DELETE /app/auth/logout invalidates the JWT
 * server-side) and the local session (clearToken + kfe:auth-changed). This is
 * the entry point for every learner-initiated sign-out (Settings row, header
 * menu). Brief 191.
 *
 * Order is load-bearing: crmLogout() MUST run BEFORE clearToken(), because
 * crm-client reads the Bearer out of this store. Clear first and crmLogout()
 * short-circuits to { kind: "session_expired", message: "No active session" }
 * with no network call at all, leaving the server session live for the rest of
 * the JWT TTL (2h) —
 * the phantom-session accumulation Brief 191 exists to stop.
 *
 * Server failures are non-fatal: local state is cleared regardless. Leaving
 * the learner stuck signed-in locally because the server didn't answer is
 * worse than orphaning a session that expires in at most two hours. The
 * failure is logged, never rethrown, and never retried.
 *
 * Mirrors iOS's order (KFEDeactivateDeviceMgr.m: server round-trip first,
 * local state flipped only after). iOS refuses to flip local state on a
 * server failure — correct there, because iOS's operation is an irreversible
 * device deactivation, not a session logout. See Brief 191 for the reasoning
 * behind the web divergence.
 *
 * @returns {Promise<{ serverInvalidated: boolean }>}
 */
export async function signOutFull() {
  // Brief 025: give the sync engine one best-effort chance to ship the outbox
  // before the credential goes away — after this returns, crm-client has no
  // Bearer to attach and every push short-circuits. Deliberately NOT awaited
  // past its own listener: Brief 191's principle is that sign-out never blocks
  // on a server call, and an unflushed row is not lost — it stays in the durable
  // outbox and ships on the next login.
  try {
    document.dispatchEvent(new Event("kfe:sync-teardown"));
  } catch { /* DOM-free context — nothing to notify */ }

  let serverInvalidated = false;
  try {
    const res = await crmLogout();
    serverInvalidated = res.success === true;
    if (!res.success) {
      console.warn("[auth-store] crmLogout failed — clearing local session anyway", res.error);
    }
  } catch (err) {
    console.warn("[auth-store] crmLogout threw — clearing local session anyway", err);
  }
  signOut();
  return { serverInvalidated };
}
